import React from 'react';
import {
    ExamStatus,
    SmsSignModal, ExamDetailsTabs,
} from 'components';
import { IExamDetails } from '../../api/ApiClient';
import { connect } from 'react-redux';
import { Dispatch, IRootState } from '../../redux/store/configureStore';
import { useLoading } from 'hooks';
import { Button } from 'react-bootstrap';

const ExamDetailsContainer: React.FC<Props> = (
  {
      examId,
      onFetchExamDetails,
      getExamApplications,
      onClearExamApplications,
      onClearExamDetails,
      setErrorHandler,
      examDetails,
      examApplicationsList,
      apiError,
      auth,
      smsCodeStatus,
      ...props
  },
) => {
    const [sendSmsProcessing, sendCode] = useLoading((captcha?: string) => props.sendCodeForApproveExam({ examId, body: { captcha } }));
    const [showSendSms, setShowSendSms] = React.useState(false);
    React.useEffect(() => {
        const fetch = async () => {
            const res = await onFetchExamDetails(examId);

            if (res) {
                await getExamApplications({ examId, pageSize: 500 });
            }
        };

        fetch();

        return () => {
            onClearExamApplications();
            onClearExamDetails();
        };
    }, [examId]);

    const handleOpenSignExamModal = () => {
        sendCode();
        setShowSendSms(true);
    };

    const handleSubmitCode = (code: string, captcha?: string) => {
        props.approveExam({ examId, body: { code, captcha } }).then(success => setShowSendSms(!success));
    };

    const renderApproveExam = (ed: IExamDetails) => auth && !ed.examCreationProgress.find(p => p.expertId === auth.profile.id) && ed.status === ExamStatus.AwaitingExpertConfirmation && (
      <>
          <Button className="mb-50 ml-30" onClick={handleOpenSignExamModal}>
              Одобрить проведение мероприятия
          </Button>
          <SmsSignModal
            title="Одобрение проведения аккредитации"
            phoneText="Для одобрения проведения аккредитационного мероприятия, введите код из СМС-сообщения, которое было отправлено на номер"
            submitButtonText="Подтвердить"
            onSubmit={handleSubmitCode}
            onSendPasswordAgain={sendCode}
            processing={sendSmsProcessing}
            onHide={() => setShowSendSms(false)}
            show={showSendSms}
            phone={auth.profile.phone}
            apiError={apiError}
            waitingSeconds={smsCodeStatus && smsCodeStatus.waitingSeconds}
          />
      </>
    );

    return examDetails ? (
      <ExamDetailsTabs
        readonly
        tab="info"
        examDetails={examDetails}
        examApplicationsList={examApplicationsList || undefined}
        applicantsTabProps={{
            handleApplicantsListChange: getExamApplications,
            handleApplicantClick: () => true,
        }}
        error={apiError}
        renderAppendForInfo={renderApproveExam}
        examUnAssignedApplicationsList={{
            fetch: props.fetchExamUnAssignedApplicationsList,
            clear: props.clearExamUnAssignedApplicationsList,
            info: props.examUnAssignedApplicationsList,
        }}
        expertsForDayList={{
            fetch: props.getExpertsForDayAsync,
            clear: props.clearExpertsForDayList,
            info: props.expertsForDayList,
        }}
        examApplicationsForDay={{
            fetch: props.fetchExamApplicationsForDayAsync,
            clear: props.clearExamApplicationsForDay,
            info: props.examApplicationsForDayList,
        }}
        experts={{
            fetch: props.getExpertsAsync,
            clear: props.clearExpertsList,
            info: props.experts,
        }}
        unratedExamApplications={{
            fetch: props.fetchUnratedExamApplications,
            clear: props.clearUnratedExamApplicationsList,
            info: props.unratedExamApplicationsList,
        }}
        applicantDetails={{
            fetch: props.fetchExamApplicationDetails,
            clear: props.clearExamApplicationDetails,
            info: props.examApplicationDetails,
        }}
        examApplicationsForDayAdmit={{
            fetch: props.fetchExamApplicationsForDayAdmitAsync,
            clear: props.clearExamApplicationsListForAdmit,
            info: props.examApplicationsListForAdmit,
        }}
      />
    ) : null;
};

const mapState = (state: IRootState) => ({
    examDetails: state.exams.examDetails,
    smsCodeStatus: state.exams.smsCodeStatus,
    examApplicationsList: state.examApplications.examApplications,
    auth: state.auth.auth,
    apiError: state.errors.error,
    examUnAssignedApplicationsList: state.examApplications.examUnAssignedApplicationsList,
    expertsForDayList: state.experts.expertsForDayList,
    experts: state.experts.experts,
    examApplicationsForDayList: state.examApplications.examApplicationsForDayList,
    unratedExamApplicationsList: state.examApplications.unratedExamApplicationsList,
    examApplicationDetails: state.examApplications.examApplicationDetails,
    examApplicationsListForAdmit: state.examApplications.examApplicationsListForAdmit,
});

const mapDispatch = (dispatch: Dispatch) => ({
    onClearExamApplications: dispatch.examApplications.clearExamApplications,
    getExamApplications: dispatch.examApplications.getExamApplications,
    onClearExamDetails: dispatch.exams.clearExamDetails,
    onFetchExamDetails: dispatch.exams.fetchExamDetails,
    setErrorHandler: dispatch.errors.setErrorHandler,
    sendCodeForApproveExam: dispatch.exams.sendCodeForApproveExam,
    approveExam: dispatch.exams.approveExam,

    clearExamUnAssignedApplicationsList: dispatch.examApplications.clearExamUnAssignedApplicationsList,
    fetchExamUnAssignedApplicationsList: dispatch.examApplications.fetchExamUnAssignedApplicationsList,

    getExpertsForDayAsync: dispatch.experts.getExpertsForDayAsync,
    clearExpertsForDayList: dispatch.experts.clearExpertsForDayList,

    fetchExamApplicationsForDayAsync: dispatch.examApplications.fetchExamApplicationsForDayAsync,
    clearExamApplicationsForDay: dispatch.examApplications.clearExamApplicationsForDay,

    getExpertsAsync: dispatch.experts.getExperts,
    clearExpertsList: dispatch.experts.clearExpertsList,

    fetchUnratedExamApplications: dispatch.examApplications.fetchUnratedExamApplications,
    clearUnratedExamApplicationsList: dispatch.examApplications.clearUnratedExamApplicationsList,

    fetchExamApplicationDetails: dispatch.examApplications.fetchExamApplicationDetails,
    clearExamApplicationDetails: dispatch.examApplications.clearDetails,

    fetchExamApplicationsForDayAdmitAsync: dispatch.examApplications.fetchExamApplicationsForDayAdmitAsync,
    clearExamApplicationsListForAdmit: dispatch.examApplications.clearExamApplicationsListForAdmit,
});

export default connect(mapState, mapDispatch)(ExamDetailsContainer);

interface ExamsDetailsOwnProps {
    examId: string;
}

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>

type Props = connectedProps & ExamsDetailsOwnProps;
